import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { 
  setIsUserLoggedIn, 
  setAuthType, 
  setApiKey, 
  setUserId,
  setGoogleData,
  setUserNickname,
  setUserEmail,
  setUserAvatar,
  setIsAdmin,
  setSessionRestoreSettled,
  AuthType 
} from 'redux/slices/userDataSlice';
import { getAccountDetails } from 'api/GoogleAuth';
import { clearAuthSession } from 'utils/auth/clearAuthSession';
import { classifyStoredSession } from 'utils/auth/sessionRestore';

export const useAuthInitialization = () => {
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector((state: RootState) => state.userData.isUserLoggedIn);

  useEffect(() => {
    const initializeAuth = async () => {
      // The shared classifier (utils/auth/sessionRestore) decides what a
      // stored session is; this path restores from its localStorage key, never
      // from a stringified null or the site's anonymous key.
      const apiKey = classifyStoredSession()?.storedApiKey ?? null;
      const userId = localStorage.getItem('user_id');
      const authType = localStorage.getItem('auth_type') as AuthType;
      const googleData = localStorage.getItem('google_data');
      const userNickname = localStorage.getItem('user_nickname');
      const userEmail = localStorage.getItem('user_email');

      // Minimum requirement - only apiKey to load into Redux
      if (apiKey) {
        // Restore state from localStorage
        dispatch(setIsUserLoggedIn(true));
        dispatch(setApiKey(apiKey));
        
        if (userId) dispatch(setUserId(parseInt(userId)));
        if (authType) dispatch(setAuthType(authType));

        // Restore user data
        if (userNickname) dispatch(setUserNickname(userNickname));
        if (userEmail) dispatch(setUserEmail(userEmail));
        
        // Restore Google data if available
        if (googleData && authType === AuthType.google) {
          try {
            const parsedGoogleData = JSON.parse(googleData);
            dispatch(setGoogleData(parsedGoogleData));
            dispatch(setUserAvatar(parsedGoogleData.picture));
          } catch (error) {
            // console.error('Error parsing Google data:', error);
          }
        }

        // Get current account data from server
        try {
          const accountDetails = await getAccountDetails(apiKey);
          if (accountDetails.status === 'success') {
            // The server is the only authority on admin status. An absent field
            // resolves to false in the reducer, never to "unknown".
            dispatch(setIsAdmin(accountDetails.data.is_admin));
            // Sync Google data with server truth. When the account has none
            // (e.g. an email-only account returns google_data: null), clear the
            // local copy too — otherwise a leftover google_data would keep
            // rendering a previous Google identity restored above.
            const serverGoogleData = accountDetails.data.google_data ?? null;
            dispatch(setGoogleData(serverGoogleData));
            if (serverGoogleData) {
              localStorage.setItem('google_data', JSON.stringify(serverGoogleData));
            } else {
              localStorage.removeItem('google_data');
              dispatch(setUserAvatar(null));
            }
          }
        } catch (error) {
          // A 401 only reaches here once the api-key recovery
          // (utils/auth/recoverApiKey) has failed or does not apply — a
          // recovered key is replayed and resolves above. Drop just the auth
          // session, never the whole localStorage (language, consent, referral).
          if (error.response?.status === 401) {
            clearAuthSession();
            dispatch(setIsAdmin(false));
            dispatch(setIsUserLoggedIn(false));
            dispatch(setApiKey(''));
            dispatch(setUserId(''));
            dispatch(setAuthType(null));
            dispatch(setGoogleData(null));
            // The restore has an outcome (signed out): guards stop waiting.
            dispatch(setSessionRestoreSettled(true));
          }
        }
      }
    };

    // Initialize only if user is not yet logged in
    if (!isUserLoggedIn) {
      initializeAuth();
    }
  }, [dispatch, isUserLoggedIn]);
};
